// Lastslate live inference + force-syntax for the editor.
//
// Two distinct mechanisms:
//
//   1. **Inference** — after every transaction that changes a block's
//      text, inspect the block and flip its type if the text matches a
//      stronger classification (INT./EXT. → scene-heading, CUT TO: →
//      transition, etc.). Implemented as a `Plugin.appendTransaction`
//      hook because input rules in PM CONSUME the typed character when
//      they return a transaction — that's correct for transformations
//      (force syntax below) but wrong for live re-classification,
//      where we want the character to remain in the new block.
//
//   2. **Force syntax** — leading `@`/`!`/`.`/`>` in an empty action
//      block consumes the marker and rewrites the block as the forced
//      type with `forced: true`. Implemented as an `InputRule` because
//      this IS a transformation: the marker is removed, not preserved.
//
// Mirrors the canonical pure inference rules at
// lib/editor/inference.ts — keep the two in sync.

import { InputRule, inputRules } from 'prosemirror-inputrules';
import { Plugin, TextSelection } from 'prosemirror-state';
import { setBlockType } from 'prosemirror-commands';
import { schema } from './schema.js';
import { palettePluginKey, PALETTE_OPEN_META } from './element-palette.js';

const STORAGE_TO_NODE = {
  'scene-heading': 'scene_heading',
  action: 'action',
  character: 'character',
  parenthetical: 'parenthetical',
  dialogue: 'dialogue',
  transition: 'transition',
  shot: 'shot',
  centered: 'centered',
};

const NODE_TO_STORAGE = Object.fromEntries(Object.entries(STORAGE_TO_NODE).map(([s, n]) => [n, s]));

const KNOWN_TRANSITIONS = new Set([
  'CUT TO:',
  'FADE TO:',
  'FADE OUT.',
  'DISSOLVE TO:',
  'SMASH CUT TO:',
  'MATCH CUT TO:',
  'JUMP CUT TO:',
  'FADE IN:',
]);

const SCENE_INTRO_RE = /^(?:INT\.\/EXT\.|INT\.|EXT\.|I\/E\s)/i;

function startsWithSceneIntro(line) {
  return SCENE_INTRO_RE.test(line);
}

function isAllUppercaseNoLower(line) {
  return line.length > 0 && !/[a-z]/.test(line);
}

function isKnownTransition(line) {
  const trimmed = line.trim();
  if (!isAllUppercaseNoLower(trimmed)) return false;
  return KNOWN_TRANSITIONS.has(trimmed);
}

function isWrappedInParens(line) {
  const trimmed = line.trim();
  return trimmed.length >= 2 && trimmed.startsWith('(') && trimmed.endsWith(')');
}

// All-caps text is NOT auto-promoted to Character. See lib/editor/inference.ts
// for the rationale. Writers reach Character via Tab, Cmd-3, the `@`
// force-prefix, or the autocomplete suggester.

// Exported for unit tests.
export function inferElementType(line, previousElement) {
  if (startsWithSceneIntro(line)) return 'scene-heading';
  if (isKnownTransition(line)) return 'transition';
  if (isWrappedInParens(line)) {
    if (previousElement === 'character' || previousElement === 'dialogue') {
      return 'parenthetical';
    }
  }
  if (previousElement === 'character' || previousElement === 'parenthetical') {
    return 'dialogue';
  }
  return null;
}

function previousBlockStorageType(state) {
  const $from = state.selection.$from;
  if ($from.depth < 1) return null;
  const blockIndex = $from.index(0);
  if (blockIndex === 0) return null;
  const prev = $from.node(0).child(blockIndex - 1);
  return NODE_TO_STORAGE[prev.type.name] || null;
}

function currentBlockInfo(state) {
  const $from = state.selection.$from;
  if ($from.depth < 1) return null;
  const node = $from.node(1);
  return { node, storage: NODE_TO_STORAGE[node.type.name] || null };
}

// Build a setBlockType transaction without dispatching. Used inside
// appendTransaction. Returns null if the change is a no-op.
function buildTypeChangeTr(state, newStorage, extraAttrs = {}) {
  const info = currentBlockInfo(state);
  if (!info) return null;
  const newNodeName = STORAGE_TO_NODE[newStorage];
  if (!newNodeName) return null;
  const newNodeType = schema.nodes[newNodeName];
  const newAttrs = { ...info.node.attrs, ...extraAttrs };
  let outTr = null;
  setBlockType(newNodeType, newAttrs)(state, (tr) => {
    outTr = tr;
  });
  return outTr;
}

// Inference plugin: runs after every transaction that mutates the
// current block's text. Re-evaluates the block's type and appends a
// type-change transaction if inference suggests a different one.
const INFERENCE_META = 'lastslate-inference';

export function inferencePlugin() {
  return new Plugin({
    appendTransaction(transactions, oldState, newState) {
      // Only react to doc changes.
      if (!transactions.some((t) => t.docChanged)) return null;
      // Don't loop on our own type changes.
      if (transactions.some((t) => t.getMeta(INFERENCE_META))) return null;

      const info = currentBlockInfo(newState);
      if (!info || !info.storage) return null;

      // Don't re-classify on deletions. Backspacing the trailing period
      // of "AS TIME GOES BY PLAYS." would otherwise flip the block to
      // character (all-caps, no trailing punctuation). Compare the
      // current block's text length before/after; only run inference
      // when text GREW (insert / paste at end), never when it shrank
      // or stayed the same.
      const oldInfo = currentBlockInfo(oldState);
      const oldBuf = oldInfo ? oldInfo.node.textContent : '';
      const buf = info.node.textContent;
      if (buf.length <= oldBuf.length) return null;
      if (buf.length === 0) return null;
      // Fresh-block guard: long blocks and blocks with internal
      // sentence breaks are committed prose; don't re-classify.
      if (buf.length > 80) return null;
      if (/[.!?]\s+[A-Z]/.test(buf)) return null;

      const prev = previousBlockStorageType(newState);
      const inferred = inferElementType(buf, prev);
      if (!inferred || inferred === info.storage) return null;

      const tr = buildTypeChangeTr(newState, inferred);
      if (!tr) return null;
      tr.setMeta(INFERENCE_META, true);
      return tr;
    },
  });
}

// Force-syntax: replace block content with the post-marker text and
// flip the type. The marker is intentionally consumed (this is a true
// input transformation, so InputRule is the right tool).
function replaceCurrentBlockText(state, storageType, newText, attrs = {}) {
  const nodeName = STORAGE_TO_NODE[storageType];
  if (!nodeName) return null;
  const nodeType = schema.nodes[nodeName];
  if (!nodeType) return null;
  const $from = state.selection.$from;
  const before = $from.before(1);
  const after = $from.after(1);
  const cur = $from.node(1);
  const newAttrs = { ...cur.attrs, ...attrs };
  const content = newText ? schema.text(newText) : null;
  const newNode = nodeType.create(newAttrs, content);
  return state.tr.replaceWith(before, after, newNode);
}

function makeForceSyntaxRules() {
  const FORCE = [
    { prefix: '@', type: 'character' },
    { prefix: '!', type: 'action' },
    { prefix: '.', type: 'scene-heading' },
    { prefix: '>', type: 'transition' },
  ];
  return FORCE.map(({ prefix, type }) => {
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new InputRule(new RegExp(`^${escaped}(.)$`), (state, match) => {
      const info = currentBlockInfo(state);
      if (!info || info.storage !== 'action') return null;
      // match[0] is the prefix + first non-marker char (e.g. ".K" when
      // typing "." then "K" in an empty action block). The prefix is
      // already in the doc; the just-typed char is in `match[0][1]`.
      const lastTyped = match[0].slice(prefix.length);
      const rest = info.node.textContent.slice(prefix.length) + lastTyped;
      const nodeName = STORAGE_TO_NODE[type];
      const attrs = {};
      if (schema.nodes[nodeName].spec.attrs && 'forced' in schema.nodes[nodeName].spec.attrs) {
        attrs.forced = true;
      }
      const tr = replaceCurrentBlockText(state, type, rest, attrs);
      if (!tr) return null;
      const $from = state.selection.$from;
      const newPos = $from.before(1) + 1 + rest.length;
      tr.setSelection(TextSelection.near(tr.doc.resolve(newPos)));
      // Mark as inference-driven so the appendTransaction watcher
      // doesn't immediately re-classify based on the consumed marker.
      tr.setMeta(INFERENCE_META, true);
      return tr;
    });
  });
}

// `/` typed at the start of an empty Action block opens the Elements
// palette in place of the slash. PM's input rules fire BEFORE the
// typed character is inserted, so we just dispatch a tr that only
// carries the open-meta — PM uses our tr in place of the default
// insert, so the `/` never lands in the doc. Restricted to action
// blocks whose pre-insert content is empty, matching the existing
// `@`/`.`/`>`/`!` force-prefix discipline: writers can still type a
// literal `/` once a block has any other text.
function makePaletteOpenRule() {
  return new InputRule(/^\/$/, (state) => {
    const $from = state.selection.$from;
    if ($from.depth < 1) return null;
    const cur = $from.node(1);
    if (cur.type.name !== 'action') return null;
    if (cur.textContent !== '') return null;
    return state.tr.setMeta(palettePluginKey, { type: PALETTE_OPEN_META });
  });
}

export function buildInputRules() {
  return inputRules({ rules: [makePaletteOpenRule(), ...makeForceSyntaxRules()] });
}
